import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Box, Typography } from '@mui/material';
import {
  ProfileContext,
  ProfileContextProps,
} from '../../context/ProfileContext';
import { logout } from '../../services/NetworkServices';
import {
  removeApprovalStatus,
  removeToken,
  removeEmail,
  removeStageNumber,
} from '../../utils/auth-token-utils/cookie';
import { SUPERVISOR_SESSION_MARKER } from '../../hooks/useSupervisorAutoLogout';
import { AuthContext } from '../../context/AuthContext';
import LogoutIcon from '../../assets/LogoutBig.svg';
import './LogoutPopup.css';
import './Popup.css';

interface PopupProps {
  title?: React.ReactNode;
  message?: React.ReactNode;
  buttonText?: string;
}

const LogoutPopup: React.FC<PopupProps> = ({ title, message }) => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('ProfileContext must be used within a ProfileProvider');
  }
  const { handleEmail, handlePassword, fcmToken } = context;

  const { handleShowLogoutPopup } = useContext(
    ProfileContext,
  ) as ProfileContextProps;

  const navigate = useNavigate();

  // Asynchronous handleYes function
  const handleYes = async () => {
    handleEmail('');
    handlePassword('');
    try {
      const response = await logout({ fcmToken: fcmToken });
      const data = await response.data;
      console.log('data is ', data);
      if (data.status) {
        removeToken();
        removeEmail();
        removeStageNumber();
        removeApprovalStatus();
        sessionStorage.removeItem(SUPERVISOR_SESSION_MARKER);
        navigate('/signin');
        localStorage.setItem('isLoggedOut', Date.now().toString());
        console.log('successful');
      }
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Handle "No" button
  const handleNo = () => {
    // Logic for "No" button
    handleShowLogoutPopup(false);
    console.log('Logout canceled');
  };

  return (
    <Box
      sx={{
        px: { xs: '40px', sm: '30px' },
        py: 4,
        width: { xs: '80vw', sm: '60vw', md: '30vw', xl: '20vw' },
        position: 'fixed',
        top: '52%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1000,
        border: '1.4px solid transparent',
        background: `linear-gradient(black, black) padding-box,
    linear-gradient(180deg, #365f91 -4.29%, rgba(54, 95, 145, 0) 100%) border-box`,
        borderImageSlice: 1,
        borderRadius: '40px',
        pointerEvents: 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Opensans',
      }}
    >
      <Box
        sx={{
          gap: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            background: 'rgba(251, 80, 18, 1)',
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '80px',
            height: '80px',
          }}
        >
          <img alt="tickicon" src={LogoutIcon} className="logoutIcon" />
        </Box>
        <Typography
          variant="h6"
          className="popup-title"
          sx={{ lineHeight: '1.4' }}
        >
          {title || (
            <>
              Are You Sure You <br /> Want to Logout ?
            </>
          )}
        </Typography>

        <Typography variant="body2" className="popup-message">
          {message ||
            'Confirm you wish to log out of your account.  If confirmed, you must reenter your Email address and password next time you want to access your account.'}
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            className="popup-button yes-button"
            onClick={handleYes}
            sx={{
              fontFamily: 'Opensans',
              backgroundColor: 'transparent',
              '&:hover': { backgroundColor: 'rgba(251, 80, 18, 1)' },
              border: '2px solid rgba(251, 80, 18, 1)',
              borderRadius: '38px',
              textTransform: 'none',
              padding: '11px 28px',
            }}
          >
            Yes
          </Button>
          <Button
            variant="contained"
            className="popup-button"
            onClick={handleNo}
            sx={{
              fontFamily: 'Opensans',
              backgroundColor: 'rgba(251, 80, 18, 1)',
              '&:hover': { backgroundColor: 'rgba(251, 80, 18, 1)' },
              borderRadius: '38px',
              textTransform: 'none',
              padding: '11px 28px',
            }}
          >
            No
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default LogoutPopup;
